import * as React from 'react';

export interface TrackedFormState {
  dirtyFieldCount: number;
  isDirty: boolean;
  isSubmitting: boolean;
}

export interface UnsavedChangesContextValue {
  forms: ReadonlyMap<string, TrackedFormState>;
  formatDirtyFieldCount?: ((count: number) => React.ReactNode) | undefined;
  removeForm: (id: string) => void;
  updateForm: (id: string, state: TrackedFormState) => void;
}

export const cleanFormState: TrackedFormState = {
  dirtyFieldCount: 0,
  isDirty: false,
  isSubmitting: false,
};

export const CurrentFormStateContext = React.createContext<TrackedFormState>(cleanFormState);
export const UnsavedChangesContext = React.createContext<UnsavedChangesContextValue | null>(null);

export function useUnsavedChanges() {
  const context = React.useContext(UnsavedChangesContext);
  const forms = context?.forms ?? new Map<string, TrackedFormState>();
  let dirtyFieldCount = 0;
  let hasUnsavedChanges = false;

  for (const state of forms.values()) {
    if (!state.isDirty) continue;
    dirtyFieldCount += state.dirtyFieldCount;
    if (!state.isSubmitting) hasUnsavedChanges = true;
  }

  return { dirtyFieldCount, hasUnsavedChanges };
}

export function useCurrentFormState() {
  return React.useContext(CurrentFormStateContext);
}

export function useUnsavedChangesLabel(count: number) {
  const context = React.useContext(UnsavedChangesContext);
  if (count <= 0) return null;
  return (
    context?.formatDirtyFieldCount?.(count) ??
    `${String(count)} unsaved ${count === 1 ? 'change' : 'changes'}`
  );
}
